import styled from 'styled-components';
import { SyncStatus } from '../types/enums';

// Interfaces
export interface IDirectionProps {
  $isRtl: boolean;
}

export interface IStatusProps {
  $status: 'connected' | 'not_connected' | 'connecting' | 'error';
}

export interface IActiveProps {
  $isActive?: boolean;
}

// Page Container
export const PageContainer = styled.div`
  width: 100%;
  min-height: calc(100vh - 100px);
  padding-bottom: 40px;
`;

// Header Section
export const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 32px;
`;

export const PageTitle = styled.h1`
  font-weight: 700;
  font-size: 24px;
  color: #130080;
  margin: 0;
`;

export const PageDescription = styled.p`
  font-weight: 400;
  font-size: 16px;
  color: #697586;
  margin: 0;
`;

// First Connection Banner
export const FirstConnectionBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #eeebff;
  border-radius: 16px;
  padding: 24px 32px;
  margin-bottom: 32px;
`;

export const BannerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const BannerTitle = styled.h2`
  font-weight: 700;
  font-size: 20px;
  color: #130080;
  margin: 0;
`;

export const BannerDescription = styled.p`
  font-weight: 400;
  font-size: 14px;
  color: #697586;
  margin: 0;
`;

export const BannerButton = styled.button`
  background: #5b3fff;
  color: white;
  border: none;
  border-radius: 12px;
  padding: 12px 24px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #4a2fe6;
  }
`;

// Channel Cards Grid
export const ChannelsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;

`;

// Channel Card
export const ChannelCardContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0px 1px 3px rgba(16, 24, 40, 0.1);
  border: 1px solid #e3e8ef;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 24px;
  &:hover {
    box-shadow: 0px 4px 12px rgba(16, 24, 40, 0.15);
    transform: translateY(-2px);
  }
`;

export const ChannelCardHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

export const ChannelLogos = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
`;

export const MsoolLogo = styled.div`
  width: 70px;
  height: 70px;
  background: #3619ab;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 12px;
  z-index: 2;

  svg {
    width: 37px;
    height: 37px;
  }
`;

export const ConnectionLine = styled.div`
  width: 42px;
  height: 4px;
  border-radius: 35621788px;
  background: #d0d5dd;
`;

export const ChannelLogo = styled.div<{ $bgColor?: string }>`
  width: 70px;
  height: 70px;
  background: ${(props) => props.$bgColor || 'white'};
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-left: -10px;
  z-index: 1;

  img, svg {
    width: 70px;
    height: 70px;
    object-fit: contain;
  }
`;

export const StatusBadge = styled.div<IStatusProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  background: ${(props) => {
    switch (props.$status) {
      case 'connected':
        return '#ecfdf3';
      case 'connecting':
        return '#fffaeb';
      case 'error':
        return '#fef3f2';
      default:
        return '#f2f4f7';
    }
  }};
  color: ${(props) => {
    switch (props.$status) {
      case 'connected':
        return '#027a48';
      case 'connecting':
        return '#b54708';
      case 'error':
        return '#b42318';
      default:
        return '#344054';
    }
  }};
`;

export const StatusDot = styled.div<IStatusProps>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) => {
    switch (props.$status) {
      case 'connected':
        return '#12b76a';
      case 'connecting':
        return '#f79009';
      case 'error':
        return '#f04438';
      default:
        return '#667085';
    }
  }};
`;

export const ChannelStats = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: space-between;
  font-size: 14px;
  color: #697586;
`;

export const StatItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: center;
  margin-bottom: 16px;
  font-size: 14px;

`;

export const StatDivider = styled.span`
  color: #d0d5dd;
`;

export const ConnectButton = styled.button<IStatusProps>`
  width: 100%;
  padding: 14px 24px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${(props) => {
    if (props.$status === 'connected') {
      return `
        background: white;
        color: #5b3fff;
        border: 1px solid #5b3fff;
        
        &:hover {
          background: #f8f7ff;
        }
      `;
    }
    return `
      background: #5b3fff;
      color: white;
      border: none;
      
      &:hover {
        background: #4a2fe6;
      }
    `;
  }}
`;

// Connect Page Styles
export const ConnectPageContainer = styled.div`
  width: 100%;
  min-height: calc(100vh - 100px);
`;

export const ConnectPageHeader = styled.div<IDirectionProps>`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
`;

export const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #697586;
  font-size: 14px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 8px;
  transition: background 0.2s ease;

  &:hover {
    background: #f2f4f7;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const SearchInput = styled.input`
  width: 320px;
  padding: 12px 16px;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: #5b3fff;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

// Connect Step Card
export const ConnectCard = styled.div`
  max-width: 560px;
  margin: 0 auto;
  background: white;
  border-radius: 24px;
  padding: 40px;
  box-shadow: 0px 4px 24px rgba(16, 24, 40, 0.08);
`;

export const ConnectCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

export const ConnectCardLogo = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f9fafb;

  svg, img {
    width: 36px;
    height: 36px;
  }
`;

export const ConnectCardTitle = styled.h1`
  font-weight: 700;
  font-size: 24px;
  color: #130080;
  margin: 0;
`;

export const ConnectCardDescription = styled.p`
  font-size: 15px;
  color: #697586;
  line-height: 1.7;
  margin: 0 0 24px;
`;

export const TermsCheckbox = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 14px;
  color: #344054;
  cursor: pointer;
  margin-bottom: 24px;

  input {
    margin-top: 3px;
    width: 18px;
    height: 18px;
    accent-color: #5b3fff;
    cursor: pointer;
  }

  a {
    color: #5b3fff;
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const ButtonsRow = styled.div`
  display: flex;
  gap: 12px;
`;

export const PrimaryButton = styled.button<{ $disabled?: boolean }>`
  flex: 1;
  padding: 16px 97px;
  background: ${(props) => (props.$disabled ? '#d0d5dd' : '#5b3fff')};
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 14px;
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.2s ease;

  &:hover {
    background: ${(props) => (props.$disabled ? '#d0d5dd' : '#4a2fe6')};
  }
`;

export const SecondaryButton = styled.button`
  flex: 1;
  padding: 14px 24px;
  background: white;
  color: #344054;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #f9fafb;
  }
`;

// Success Step - Matches Figma design (linear gradient background)
export const SuccessBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  background: linear-gradient(135deg, rgba(91, 47, 249, 0.05) 0%, rgba(124, 58, 237, 0.05) 100%);
  border: 1px solid rgba(91, 47, 249, 0.1);
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 32px;
`;

export const SuccessLogos = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

export const SuccessCheckmark = styled.div`
  width: 18px;
  height: 18px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #06c270;
  font-size: 12px;
  font-weight: bold;
  position: absolute;
  bottom: -2px;
  left: 35px;
  z-index: 3;
  box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.25);
`;

export const SuccessContent = styled.div`
  flex: 1;
  text-align: right;
`;

export const SuccessTitle = styled.h2`
  font-weight: 500;
  font-size: 20px;
  color: #261b6a;
  margin: 0 0 8px;
  letter-spacing: -0.45px;
`;

export const SuccessDescription = styled.p`
  font-size: 14px;
  color: #666666;
  margin: 0;
  line-height: 20px;
`;

// Import Properties Step - Matches Figma design (node 1:11241)
export const ImportHeader = styled.div<IDirectionProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  border-radius: 16px;
  padding: 0 16px;
  height: 60px;
  margin-bottom: 32px;
`;

export const ImportTitle = styled.h2`
  font-weight: 400;
  font-size: 14px;
  color: #697586;
  margin: 0;
  line-height: 24px;
`;

export const ImportLaterButton = styled.button`
  background: none;
  border: none;
  color: #5b3fff;
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    opacity: 0.8;
  }
`;

export const PropertiesCard = styled.div`
  background: white;
  border-radius: 16px;
  border: 1px solid #E9E9E7;
  overflow: hidden;
`;

export const PropertiesHeader = styled.div<IDirectionProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  height: 76px;
  box-sizing: border-box;
  border-bottom: 1px solid #E9E9E7;
`;

export const PropertiesCount = styled.span`
  font-size: 14px;
  color: #697586;
  background: #f2f4f7;
  padding: 4px 12px;
  border-radius: 12px;
`;

export const PropertiesList = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

export const PropertyRow = styled.div<IActiveProps & IDirectionProps>`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid #f2f4f7;
  cursor: pointer;
  transition: background 0.2s ease;
  background: ${(props) => (props.$isActive ? '#f8f7ff' : 'transparent')};

  &:hover {
    background: #f8f7ff;
  }

  &:last-child {
    border-bottom: none;
  }
`;

export const PropertyCheckbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  border: 2px solid #d0d5dd;
  border-radius: 4px;
  background: white;
  position: relative;
  transition: all 0.2s ease;

  &:checked {
    background: #5b3fff;
    border-color: #5b3fff;
  }

  &:checked::after {
    content: '✓';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: white;
    font-size: 12px;
    font-weight: bold;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const PropertyImage = styled.div`
  width: 63px;
  height: 30px;
  background: #f2f4f7;
  border-radius: 2px;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const PropertyInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const PropertyName = styled.h4`
  font-weight: 700;
  font-size: 14px;
  color: #331860;
  margin: 0;
  white-space: nowrap;
`;

export const PropertyAddress = styled.p`
  font-size: 12px;
  color: #697586;
  margin: 0;
`;

export const PropertyId = styled.span`
  font-size: 12px;
  color: #000000;
`;

export const PropertyStatus = styled.span<{ $syncStatus?: SyncStatus; $importing?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 9999px;
  white-space: nowrap;
  background: ${(props) => {
    if (props.$importing) return '#FEF3C7';
    switch (props.$syncStatus) {
      case SyncStatus.NotSynced: return '#F3F4F6';
      case SyncStatus.Synced: return '#ECFDF5';
      case SyncStatus.InProgress: return '#FEF3C7';
      case SyncStatus.Failed: return '#FEE2E2';
      case SyncStatus.Pending: return '#E0E7FF';
      case SyncStatus.Imported: return '#5B2FF91A';
      case SyncStatus.Disconnected: return '#FEE2E2';
      default: return '#F3F4F6';
    }
  }};
  color: ${(props) => {
    if (props.$importing) return '#D97706';
    switch (props.$syncStatus) {
      case SyncStatus.NotSynced: return '#6B7280';
      case SyncStatus.Synced: return '#059669';
      case SyncStatus.InProgress: return '#D97706';
      case SyncStatus.Failed: return '#DC2626';
      case SyncStatus.Pending: return '#4F46E5';
      case SyncStatus.Imported: return '#5B2FF9';
      case SyncStatus.Disconnected: return '#DC2626';
      default: return '#6B7280';
    }
  }};
`;

export const ImportFooter = styled.div`
  margin-top: 24px;
`;

// Progress Step
export const ProgressCard = styled.div<IDirectionProps>`
  max-width: 560px;
  margin: 0 auto;
  background: white;
  border-radius: 24px;
  padding: 40px;
  box-shadow: 0px 4px 24px rgba(16, 24, 40, 0.08);
  text-align: center;
`;

export const ProgressIcon = styled.div`
  width: 80px;
  height: 80px;
  background: #f8f7ff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  font-size: 40px;
`;

export const ProgressTitle = styled.h2`
  font-weight: 700;
  font-size: 20px;
  color: #130080;
  margin: 0 0 8px;
`;

export const ProgressDescription = styled.p`
  font-size: 14px;
  color: #697586;
  margin: 0 0 32px;
`;

export const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background: #f2f4f7;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 32px;
`;

export const ProgressBarFill = styled.div<{ $progress: number }>`
  height: 100%;
  background: #5b3fff;
  border-radius: 4px;
  width: ${(props) => props.$progress}%;
  transition: width 0.3s ease;
`;

export const NotificationBanner = styled.div<IDirectionProps>`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fffaeb;
  border-radius: 12px;
  padding: 16px;
  font-size: 14px;
  color: #b54708;
  margin-bottom: 24px;
  text-align: ${(props) => (props.$isRtl ? 'right' : 'left')};

  svg {
    flex-shrink: 0;
  }
`;

export const BackArrowButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: none;
  border: none;
  cursor: pointer;
  color: #697586;
  padding: 0;
  
  [dir="rtl"] & {
    transform: rotate(180deg);
  }
  
  &:hover {
    color: #5b3fff;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

export const HeaderWithBack = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 32px;
`;

export const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// ManageChannel and UnitsMapping styles available via separate imports:
// import { ... } from './styles/ManageChannelStyles';
// import { ... } from './styles/UnitsMappingStyles';
